<template>
  <div
    :class="['template-element', { selected }]"
    :style="elementStyle"
    @click.stop="handleClick"
    @mousedown="handleMouseDown"
  >
    <!-- 文本元素 -->
    <div v-if="element.type === 'text'" class="text-element">
      {{ element.content }}
    </div>
    
    <!-- 表格元素 -->
    <div v-else-if="element.type === 'table'" class="table-element">
      <table class="element-table">
        <thead>
          <tr>
            <th 
              v-for="column in element.columns" 
              :key="column.id"
              :style="{ width: column.width + 'px' }"
            >
              {{ column.title }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr 
            v-for="(row, rowIndex) in element.data" 
            :key="rowIndex"
            :style="{ height: element.rowHeight + 'px' }"
          >
            <td 
              v-for="column in element.columns" 
              :key="column.id"
            >
              {{ row[column.id] }}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
    
    <!-- 二维码元素 -->
    <div v-else-if="element.type === 'qrcode'" class="qrcode-element">
      <canvas 
        :ref="el => qrCodeRef = el"
        class="qrcode-canvas"
      ></canvas>
    </div>
    
    <!-- 条形码元素 -->
    <div v-else-if="element.type === 'barcode'" class="barcode-element">
      <canvas 
        :ref="el => barcodeRef = el"
        class="barcode-canvas"
      ></canvas>
    </div>
    
    <!-- 其他类型元素的占位符 -->
    <div v-else class="placeholder-element">
      {{ element.type }} 元素
    </div>
    
    <!-- 选中状态的控制点 -->
    <div v-if="selected" class="selection-handles">
      <div class="handle handle-tl"></div>
      <div class="handle handle-tr"></div>
      <div class="handle handle-bl"></div>
      <div class="handle handle-br"></div>
    </div>
  </div>
</template>

<script>
import { computed, ref, onMounted, watch, nextTick } from 'vue'
import QRCode from 'qrcode'
import JsBarcode from 'jsbarcode'

export default {
  name: 'TemplateElement',
  props: {
    element: {
      type: Object,
      required: true
    },
    index: {
      type: Number,
      required: true
    },
    selected: {
      type: Boolean,
      default: false
    },
    zoom: {
      type: Number,
      default: 1
    }
  },
  emits: ['select', 'update', 'start-drag', 'drag', 'end-drag'],
  
  setup(props, { emit }) {
    const qrCodeRef = ref(null)
    const barcodeRef = ref(null)
    
    const elementStyle = computed(() => ({
      ...props.element.style,
      transform: `scale(${props.zoom})`,
      transformOrigin: 'top left'
    }))
    
    const handleClick = () => {
      emit('select', props.index)
    }
    
    const handleMouseDown = (event) => {
      if (event.button !== 0) return
      
      const startX = event.clientX
      const startY = event.clientY
      const startLeft = parseInt(props.element.style.left) || 0
      const startTop = parseInt(props.element.style.top) || 0
      
      emit('start-drag', {
        elementIndex: props.index,
        startPosition: { x: startX, y: startY }
      })
      
      const handleMouseMove = (e) => {
        const deltaX = (e.clientX - startX) / props.zoom
        const deltaY = (e.clientY - startY) / props.zoom
        
        const newLeft = Math.max(0, startLeft + deltaX)
        const newTop = Math.max(0, startTop + deltaY)
        
        const updatedElement = {
          ...props.element,
          style: {
            ...props.element.style,
            left: newLeft + 'px',
            top: newTop + 'px'
          }
        }
        
        emit('update', { index: props.index, element: updatedElement })
        emit('drag', { x: newLeft, y: newTop })
      }
      
      const handleMouseUp = () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        emit('end-drag')
      }
      
      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
    }
    
    // 生成二维码
    const generateQRCode = async () => {
      if (!qrCodeRef.value || props.element.type !== 'qrcode') return
      
      try {
        const canvas = qrCodeRef.value
        const content = props.element.content || 'https://example.com'
        const width = parseInt(props.element.style.width) || 80
        const height = parseInt(props.element.style.height) || 80
        
        canvas.width = width
        canvas.height = height
        
        await QRCode.toCanvas(canvas, content, {
          width: width,
          height: height,
          margin: 1,
          color: {
            dark: '#000000',
            light: '#FFFFFF'
          }
        })
      } catch (error) {
        console.error('生成二维码失败:', error)
      }
    }
    
    // 生成条形码
    const generateBarcode = () => {
      if (!barcodeRef.value || props.element.type !== 'barcode') return
      
      try {
        const canvas = barcodeRef.value
        const content = props.element.content || '123456789'
        const width = parseInt(props.element.style.width) || 120
        const height = parseInt(props.element.style.height) || 40
        
        canvas.width = width
        canvas.height = height
        
        JsBarcode(canvas, content, {
          format: "CODE128",
          width: 2,
          height: height - 10,
          displayValue: false,
          background: "#FFFFFF",
          lineColor: "#000000",
          margin: 5
        })
      } catch (error) {
        console.error('生成条形码失败:', error)
        // 如果生成失败，显示错误信息
        const ctx = barcodeRef.value.getContext('2d')
        ctx.fillStyle = '#FF0000'
        ctx.font = '12px Arial'
        ctx.fillText('条码错误', 10, 20)
      }
    }
    
    // 监听元素变化
    watch(() => [props.element.content, props.element.style.width, props.element.style.height], () => {
      nextTick(() => {
        if (props.element.type === 'qrcode') {
          generateQRCode()
        } else if (props.element.type === 'barcode') {
          generateBarcode()
        }
      })
    }, { immediate: false })
    
    // 组件挂载后生成码
    onMounted(() => {
      nextTick(() => {
        if (props.element.type === 'qrcode') {
          generateQRCode()
        } else if (props.element.type === 'barcode') {
          generateBarcode()
        }
      })
    })
    
    return {
      elementStyle,
      handleClick,
      handleMouseDown,
      qrCodeRef,
      barcodeRef
    }
  }
}
</script>

<style scoped>
.template-element {
  position: absolute;
  cursor: move;
  user-select: none;
  min-width: 20px;
  min-height: 20px;
}

.template-element.selected {
  z-index: 100;
}

.text-element {
  padding: 4px 8px;
  background: rgba(255, 255, 255, 0.9);
  border: 1px dashed #ccc;
  border-radius: 2px;
  line-height: 1.4;
  word-wrap: break-word;
}

.table-element {
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #999;
  border-radius: 2px;
  overflow: hidden;
}

.element-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.element-table th,
.element-table td {
  border: 1px solid #ddd;
  padding: 4px 8px;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.element-table th {
  background: #f8f9fa;
  font-weight: bold;
}

.qrcode-element {
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #999;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  overflow: hidden;
}

.qrcode-canvas {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.barcode-element {
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid #999;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  overflow: hidden;
}

.barcode-canvas {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.placeholder-element {
  padding: 8px 12px;
  background: #e9ecef;
  border: 1px dashed #adb5bd;
  border-radius: 4px;
  color: #6c757d;
  font-size: 12px;
  text-align: center;
}

.selection-handles {
  position: absolute;
  top: -4px;
  left: -4px;
  right: -4px;
  bottom: -4px;
  pointer-events: none;
}

.handle {
  position: absolute;
  width: 8px;
  height: 8px;
  background: #007bff;
  border: 1px solid #fff;
  border-radius: 50%;
  pointer-events: auto;
  cursor: pointer;
}

.handle-tl {
  top: 0;
  left: 0;
  cursor: nw-resize;
}

.handle-tr {
  top: 0;
  right: 0;
  cursor: ne-resize;
}

.handle-bl {
  bottom: 0;
  left: 0;
  cursor: sw-resize;
}

.handle-br {
  bottom: 0;
  right: 0;
  cursor: se-resize;
}

.template-element.selected {
  outline: 2px solid #007bff;
  outline-offset: 2px;
}

/* 响应式处理 */
@media (max-width: 768px) {
  .element-table {
    font-size: 11px;
  }
  
  .element-table th,
  .element-table td {
    padding: 2px 4px;
  }
  
  .handle {
    width: 12px;
    height: 12px;
  }
}
</style>
